/**
 * Разделы рабочего места и доступ к ним.
 *
 * Здесь администратор заводит CRM, сервис WhatsApp и свободный браузер, а
 * затем поимённо открывает их сотрудникам.
 *
 * Почему адрес вводится, а не зашит в код: CRM и WhatsApp живут на серверах
 * компании, и их адреса — эксплуатационный параметр. Смена адреса не должна
 * требовать пересборки приложения.
 *
 * Почему доступ поимённый, а не по роли: роль отвечает, что человек умеет, а
 * выдача — к чему он допущен. Смешать их значило бы открывать доступ к чужой
 * CRM повышением в должности.
 */

import { useCallback, useEffect, useState } from "react";
import type { UserDto, UserSummaryDto } from "@tw/shared-types";
import { useSession } from "../state/session.tsx";
import type { AccountFolderDto, ManagedSectionDto } from "../lib/api.js";
import {
  ADMIN_HINT,
  ADMIN_PANEL,
  ADMIN_PANEL_TITLE,
  ADMIN_SUBTITLE,
} from "./adminPanel.js";
import { PlusIcon, TrashIcon } from "./icons.tsx";
import { SECTIONS_CHANGED } from "../lib/sectionsEvent.js";
import { cn } from "@/lib/utils";
import { SectionAccessEditor } from "./SectionAccessEditor.tsx";
import { FIELD } from "./controls.js";

type AnyUser = UserDto | UserSummaryDto;

const KIND_LABELS: Record<string, string> = {
  EMBEDDED: "Встроенный интерфейс",
  BROWSER: "Браузер",
};

export function SectionsPanel({ users }: { users: AnyUser[] }) {
  const { api } = useSession();

  const [items, setItems] = useState<ManagedSectionDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  /*
   * Папки нужны редактору доступов, и грузятся они здесь, а не в нём: иначе
   * каждый развёрнутый раздел слал бы свой запрос за одним и тем же деревом.
   */
  const [folders, setFolders] = useState<AccountFolderDto[]>([]);

  useEffect(() => {
    api
      .accountFolders()
      .then((tree) => setFolders(tree.folders))
      // Недоступное дерево папок не должно ломать экран разделов: выдача по
      // ролям и людям работает и без него.
      .catch(() => setFolders([]));
  }, [api]);

  const activeUsers = users.filter((u) => u.status === "ACTIVE");

  const [key, setKey] = useState("");
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<"EMBEDDED" | "BROWSER">("EMBEDDED");
  const [url, setUrl] = useState("");

  const load = useCallback(async () => {
    try {
      const { items: list } = await api.manageSections();
      setItems(list);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Не удалось загрузить разделы",
      );
    }
  }, [api]);

  /*
   * Оповещение оболочки о том, что состав разделов изменился.
   *
   * Событие окна, а не проброс колбэка через три уровня. Разделы меняются
   * редко и не входят в состояние ни одного промежуточного компонента —
   * тянуть ради этого проп от App через SettingsView значило бы добавить
   * связь там, где её нет по смыслу.
   *
   * Без оповещения новая вкладка появлялась бы только после перезагрузки
   * страницы: список разделов читается при входе.
   */
  const announce = (): void => {
    window.dispatchEvent(new Event(SECTIONS_CHANGED));
  };

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.createSection({
        // Пустой ключ не отправляем вовсе: сервер соберёт его из названия.
        // Отправить пустую строку значило бы не пройти проверку формата.
        ...(key.trim() ? { key: key.trim() } : {}),
        title: title.trim(),
        kind,
        ...(url.trim() ? { url: url.trim() } : {}),
      });
      setKey("");
      setTitle("");
      setUrl("");
      await load();
      announce();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Не удалось создать раздел",
      );
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      announce();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Действие не удалось");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <section className={ADMIN_PANEL}>
        <h2 className={ADMIN_PANEL_TITLE}>Новый раздел</h2>
        <p className={ADMIN_HINT}>
          Встроенный интерфейс открывается по фиксированному адресу — так
          подключают CRM и сервис WhatsApp. Браузер даёт адресную строку и
          произвольные страницы.
        </p>

        <form
          className="mt-1 grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="grid gap-1 text-xs font-medium text-muted-foreground">
              Название
              <input
                className={FIELD}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="CRM"
                maxLength={64}
              />
            </label>

            <label className="grid gap-1 text-xs font-medium text-muted-foreground">
              Ключ — необязательно
              <input
                className={FIELD}
                value={key}
                onChange={(e) => setKey(e.target.value.toLowerCase())}
                placeholder="соберётся из названия"
                maxLength={32}
              />
              {/*
                Пояснение, а не просто «необязательно»: иначе поле выглядит
                как то, которое лучше заполнить на всякий случай, — и его
                заполняют, придумывая латиницу для «Бухгалтерии».
              */}
              <span className="text-[11px] font-normal leading-relaxed text-muted-foreground">
                Внутреннее имя для ссылок. Оставьте пустым — соберём из
                названия. Пригодится, только если на раздел ссылаются извне.
              </span>
            </label>
          </div>

          <label className="grid gap-1 text-xs font-medium text-muted-foreground">
            Вид
            <select
              className={FIELD}
              value={kind}
              onChange={(e) =>
                setKind(e.target.value as "EMBEDDED" | "BROWSER")
              }
            >
              <option value="EMBEDDED">Встроенный интерфейс</option>
              <option value="BROWSER">Браузер</option>
            </select>
          </label>

          <label className="grid gap-1 text-xs font-medium text-muted-foreground">
            Адрес
            {kind === "BROWSER" ? " (стартовая страница, необязательно)" : ""}
            <input
              className={FIELD}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://crm.example.com"
              maxLength={2048}
            />
          </label>

          <button
            type="submit"
            className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-full border-transparent bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-45"
            disabled={busy || title.trim().length === 0}
          >
            <PlusIcon className="size-4" />
            Добавить раздел
          </button>
        </form>
      </section>

      {error && (
        <p
          role="alert"
          className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      {items.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card px-6 py-10 text-center">
          <p className="text-sm font-medium text-heading">Разделов пока нет</p>
          <p className="max-w-[44ch] text-xs leading-relaxed text-muted-foreground">
            Заведите CRM и сервис WhatsApp, указав их адреса, — они появятся
            вкладками у тех сотрудников, кому вы откроете доступ.
          </p>
        </div>
      )}

      {items.map((section) => {
        const isOpen = openId === section.id;

        return (
          <section key={section.id} className={ADMIN_PANEL}>
            <div className="flex flex-wrap items-center gap-2">
              <span
                aria-hidden
                className={cn(
                  "status-dot",
                  section.enabled ? "text-dot-done" : "text-muted-foreground",
                )}
              />
              <h3 className={ADMIN_SUBTITLE}>{section.title}</h3>
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                {KIND_LABELS[section.kind] ?? section.kind}
              </span>

              <button
                type="button"
                className="ml-auto rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent disabled:opacity-45"
                disabled={busy}
                onClick={() =>
                  void act(() =>
                    api.updateSection(section.id, {
                      enabled: !section.enabled,
                    }),
                  )
                }
              >
                {section.enabled ? "Выключить" : "Включить"}
              </button>

              <button
                type="button"
                className="grid size-8 flex-none place-items-center rounded-full border-transparent bg-transparent text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive disabled:opacity-45"
                aria-label={`Удалить раздел ${section.title}`}
                disabled={busy}
                onClick={() => {
                  // Удаление уносит и выдачи доступа. Подтверждение обязательно
                  // и называет последствие, а не спрашивает «вы уверены».
                  const ok = window.confirm(
                    `Удалить раздел «${section.title}»?\n\n` +
                      `Доступ к нему потеряют ${section.access.length} сотрудников. ` +
                      "Действие необратимо.",
                  );
                  if (ok) void act(() => api.deleteSection(section.id));
                }}
              >
                <TrashIcon className="size-4" />
              </button>
            </div>

            <p className="truncate font-mono text-[11px] text-muted-foreground">
              {section.url ?? "адрес не задан"}
            </p>


            {/*
              Поля формы входа — ЗАПАСНОЙ путь.

              Обычную форму оболочка находит сама, и настраивать здесь ничего
              не нужно. Первая версия требовала эти селекторы обязательно, и
              автовход из-за этого не работал ни у кого: чтобы его включить,
              администратор должен был сходить в инструменты разработчика, а
              сотрудник до тех пор видел пустую панель без объяснений.

              Заполнять это стоит только когда автоподстановка не сработала: у
              системы вход в два шага, поле пароля появляется после нажатия
              кнопки, форм на странице несколько.
            */}
            <details className="mt-1 rounded-xl border border-border bg-secondary/40 px-3 py-2">
              <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                Поля формы входа{" "}
                {section.loginSelector && section.passwordSelector
                  ? "— заданы вручную"
                  : "— определяются автоматически"}
              </summary>

              <div className="mt-2 flex flex-col gap-2">
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  Обычно заполнять не нужно: приложение само находит поля логина
                  и пароля на странице входа. Заполните, только если автовход в
                  эту систему не срабатывает.
                </p>
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  CSS-селекторы полей, их видно в инструментах разработчика.
                  Например:{" "}
                  <code className="font-mono">input[name=&quot;email&quot;]</code>,{" "}
                  <code className="font-mono">#password</code>,{" "}
                  <code className="font-mono">button[type=&quot;submit&quot;]</code>.
                </p>

                <LoginFormEditor
                  section={section}
                  busy={busy}
                  onSave={(values) =>
                    void act(() => api.updateSection(section.id, values))
                  }
                />
              </div>
            </details>

            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">
                Доступ открыт: {section.access.length}
              </span>
              <button
                type="button"
                className="rounded-full border border-brand bg-card px-3 py-1 text-xs font-medium text-brand transition-colors hover:bg-secondary"
                onClick={() => setOpenId(isOpen ? null : section.id)}
              >
                {isOpen ? "Свернуть" : "Настроить доступ"}
              </button>
            </div>

            {isOpen && (
              <div className="mt-1 rounded-xl border border-border bg-secondary/40 p-3">
                <SectionAccessEditor
                  section={section}
                  folders={folders}
                  users={activeUsers}
                  busy={busy}
                  onGrant={(subject) =>
                    void act(() => api.grantSection(section.id, subject))
                  }
                  onRevoke={(accessId) =>
                    void act(() => api.revokeSection(section.id, accessId))
                  }
                />
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

/**
 * Редактор селекторов формы входа.
 *
 * Три поля сохраняются вместе одной правкой: по отдельности они бессмысленны —
 * логин без пароля подставить нельзя, — и три кнопки «Сохранить» подряд
 * предлагали бы держать раздел в заведомо нерабочем состоянии.
 *
 * Пустое поле означает «убрать», а не «не менять»: администратор, стерший
 * селектор, ожидает, что автоподстановка выключится.
 */
function LoginFormEditor({
  section,
  busy,
  onSave,
}: {
  section: ManagedSectionDto;
  busy: boolean;
  onSave: (values: {
    loginSelector: string | null;
    passwordSelector: string | null;
    submitSelector: string | null;
  }) => void;
}) {
  const [login, setLogin] = useState(section.loginSelector ?? "");
  const [password, setPassword] = useState(section.passwordSelector ?? "");
  const [submit, setSubmit] = useState(section.submitSelector ?? "");

  const changed =
    login !== (section.loginSelector ?? "") ||
    password !== (section.passwordSelector ?? "") ||
    submit !== (section.submitSelector ?? "");

  const blank = (value: string): string | null => value.trim() || null;

  return (
    <div className="flex flex-col gap-2">
      <input
        value={login}
        onChange={(e) => setLogin(e.target.value)}
        placeholder="Селектор поля логина"
        aria-label="Селектор поля логина"
        className={cn(FIELD, "font-mono text-xs")}
      />
      <input
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Селектор поля пароля"
        aria-label="Селектор поля пароля"
        className={cn(FIELD, "font-mono text-xs")}
      />
      <input
        value={submit}
        onChange={(e) => setSubmit(e.target.value)}
        // Необязателен: без него отправляем саму форму, в которой лежит поле
        // пароля. Это работает в большинстве случаев и избавляет от лишней
        // настройки.
        placeholder="Селектор кнопки входа — необязательно"
        aria-label="Селектор кнопки входа"
        className={cn(FIELD, "font-mono text-xs")}
      />

      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded-full border border-brand bg-card px-3 py-1 text-xs font-medium text-brand transition-colors hover:bg-secondary disabled:opacity-45"
          disabled={busy || !changed}
          onClick={() =>
            onSave({
              loginSelector: blank(login),
              passwordSelector: blank(password),
              submitSelector: blank(submit),
            })
          }
        >
          Сохранить форму входа
        </button>

        {/* Половина настройки не работает вовсе, и молчать об этом нельзя:
            администратор ушёл бы с экрана в уверенности, что настроил. */}
        {Boolean(login.trim()) !== Boolean(password.trim()) && (
          <span className="text-[11px] text-destructive">
            Нужны оба поля: логин и пароль
          </span>
        )}
      </div>
    </div>
  );
}
